/**
 * Every feature flag this application knows about, with the value it takes when
 * PostHog has no answer.
 *
 * Declared here rather than passed around as loose strings for two reasons:
 *
 *  - A typo in a flag key is otherwise indistinguishable from a flag that is
 *    switched off. `pnpm typecheck` catches it instead.
 *  - A flag needs a defined value for the cases PostHog cannot serve: before
 *    the browser has loaded flags, when the project is unconfigured, and when
 *    the evaluation call fails. That value belongs next to the flag, once, not
 *    at each of the dozen call sites.
 *
 * **Defaults are the safe state, not the intended state.** A half-finished
 * feature defaults to `false` so that an outage at PostHog does not ship it;
 * a kill switch for something already live defaults to `true` so the same
 * outage does not turn it off.
 *
 * This module is imported by the browser bundle as well (`@starter/api/flags`),
 * so it must stay free of server imports — plain data only.
 */
export const featureFlagDefaults = {
  /**
   * Example flag, and the one the tests exercise. Replace it with a real one;
   * deleting the last entry leaves `FeatureFlagKey` as `never` and every call
   * site fails to compile, which is the intended nudge to keep this list real.
   */
  /**
   * The four fields a brand gained in #54 — what the business does, its
   * vertical, its offers, and whether advertising may quote reviews.
   *
   * Off by default because the columns shipped before the screens that fill
   * them: the flag is what makes undoing this a switch rather than a deploy,
   * which is the whole reason a schema change ships behind one
   * (`docs/BRANCHING.md`). It gates the interface, not the API — the columns
   * exist either way, and nothing can reach them while no screen offers them.
   */
  'brand-extra-fields': false,
  /**
   * The eight fields a product gained in #60 — what to say about the thing, as
   * opposed to what the thing is: benefits, specs, the call to action, proof
   * points, permitted claims, the scene, the packshot subject and the category
   * tags. The audience section rides with them.
   *
   * **On by default, and that is the deliberate part.** The default is the
   * safe state, and by the time this shipped the unsafe state had moved: the
   * columns were already live (#60), the screens were complete and their
   * behaviour tested, and a product page permanently missing four of its seven
   * sections is not a safe state — it is a product that does not look like
   * itself, in a repository whose acceptance test is looking like itself.
   *
   * What the flag is still for is the thing `docs/BRANCHING.md` asks of it:
   * undoing this is a switch rather than a deploy. What it gates is the
   * *sections*, not the page — with it off, description, price and
   * photographs are a coherent screen rather than a broken one.
   */
  'product-enrichment-fields': true,
} as const satisfies Record<string, boolean>;

export type FeatureFlagKey = keyof typeof featureFlagDefaults;

/** Scopes evaluation to flags this code actually branches on. */
export const featureFlagKeys = Object.keys(featureFlagDefaults) as FeatureFlagKey[];

export function defaultFor(key: FeatureFlagKey): boolean {
  return featureFlagDefaults[key];
}
